import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';

import styled from 'styled-components';

import { LoadingIndicator } from 'atoms';

import { StudentColors } from './studentColors';

interface ITabStateViewProps {
  loading: boolean;
  error?: ApolloError;
  isEmpty: boolean;
  onRetry: () => void;
  children: React.ReactNode;
}

/**
 * Shared terminal-state wrapper for Student card tabs/blocks (Area 7, Req 19).
 * Direct port of the GroupDetail `TabStateView`.
 *
 * Distinguishes the three terminal states a query can land in, kept visually
 * distinct: loading (initial load) → error (`ApolloError`, danger panel + Retry
 * calling `onRetry`/`refetch`) → empty (resolved, no rows) → populated children.
 * All copy resolves through i18next under `pages.studentDetail.state.*`; colors
 * come from `StudentColors` tokens (no hex).
 */
export const TabStateView = memo(
  ({ loading, error, isEmpty, onRetry, children }: ITabStateViewProps) => {
    const { t } = useTranslation();

    if (loading) {
      return (
        <CenterState>
          <LoadingIndicator />
        </CenterState>
      );
    }

    if (error) {
      return (
        <ErrorPanel role="alert">
          <ErrorMessage>{t('pages.studentDetail.state.error')}</ErrorMessage>
          <RetryButton type="button" onClick={onRetry}>
            {t('pages.studentDetail.state.retry')}
          </RetryButton>
        </ErrorPanel>
      );
    }

    if (isEmpty) {
      return (
        <EmptyPanel>
          <EmptyMessage>{t('pages.studentDetail.state.empty')}</EmptyMessage>
        </EmptyPanel>
      );
    }

    return <>{children}</>;
  },
);

const CenterState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  padding: 24px;
`;

const ErrorPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-height: 160px;
  padding: 24px;
  text-align: center;
  border: 1px solid ${StudentColors.danger};
  border-radius: 8px;
  background: ${StudentColors.surface};
`;

const ErrorMessage = styled.div`
  color: ${StudentColors.danger};
  font-size: 14px;
  font-weight: 600;
`;

const RetryButton = styled.button`
  border: 1px solid ${StudentColors.accent};
  background: ${StudentColors.accent};
  color: ${StudentColors.surface};
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover:not(:disabled) {
    background: ${StudentColors.accentStrong};
    border-color: ${StudentColors.accentStrong};
  }
`;

const EmptyPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  padding: 24px;
  text-align: center;
  border: 1px dashed ${StudentColors.border};
  border-radius: 8px;
  background: ${StudentColors.surfaceMuted};
`;

const EmptyMessage = styled.div`
  color: ${StudentColors.textMuted};
  font-size: 13px;
  font-weight: 500;
`;
